import React, {useState, useRef} from "react";
import {Button, Col, DatePicker, Drawer, Form, Input, InputNumber, Radio, Row} from "antd";
import dayjs from "dayjs";

import InputMoney from "../../../components/billings/InputMoney";
import {messageType, showMessages} from "../../../helpers/message";
import {formatTimestampStartOfDay, formatTimestampEndOfDay} from "../../../helpers/misc";
import {updatePromotion} from "../../../helpers/promotion";
import {greaterThanOrEqualOne, greaterThanZero, nothingToUpdate} from "../../../helpers/text";
import {dateFormat} from "../../../lib/time";

import "./PromotionAddDrawer.less";

const PromotionEditDrawer = ({
                                promoID,
                                oldPromoName,
                                oldPromoCode,
                                oldPromoStartedAt,
                                oldPromoEndedAt,
                                oldPromoDefaultAmount,
                                oldPromoCreditEndDate,
                                oldPromoCreditDuration,
                                oldPromoMaxCredits,
                                oldPromoMaxCreditsAccount,
                                setTableRefresh
                             }) => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);
    const mobileMode = window.innerWidth <= 768;
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [radioSelected, setRadioSelected] = useState('');

    // Promotion update state.
    const [newPromoName, setNewPromoName] = useState(null);
    const [newPromoCode, setNewPromoCode] = useState(null);
    const [newPromoStartedAt, setNewPromoStartedAt] = useState(null);
    const [newPromoEndedAt, setNewPromoEndedAt] = useState(null);
    const [newPromoDefaultAmount, setNewPromoDefaultAmount] = useState(null);
    const [newPromoCreditEndDate, setNewPromoCreditEndDate] = useState(null);
    const [newPromoCreditDuration, setNewPromoCreditDuration] = useState(null);
    const [newPromoMaxCredits, setNewPromoMaxCredits] = useState(null);
    const [newPromoMaxCreditAccount, setNewPromoMaxCreditAccount] = useState(null);

    const onClose = () => {
        setOpen(false);
        form.resetFields();
    }

    const onOpen = () => {
        setOpen(true);
        form.setFieldsValue({
            name: oldPromoName,
            promo_code: oldPromoCode,
            started_at: oldPromoStartedAt,
            ended_at: oldPromoEndedAt,
            default_amount: oldPromoDefaultAmount,
            credits_default_ended_at: oldPromoCreditEndDate,
            credits_default_duration: oldPromoCreditDuration,
            credits_max_num_total: oldPromoMaxCredits,
            credits_max_num_account: oldPromoMaxCreditsAccount,
            promo_credit_expire: oldPromoCreditEndDate ? 'date' : 'duration',
        });

        setNewPromoName(oldPromoName);
        setNewPromoCode(oldPromoCode);
        setNewPromoStartedAt(oldPromoStartedAt);
        setNewPromoEndedAt(oldPromoEndedAt);
        setNewPromoDefaultAmount(oldPromoDefaultAmount);
        setNewPromoCreditEndDate(oldPromoCreditEndDate);
        setNewPromoCreditDuration(oldPromoCreditDuration);
        setNewPromoMaxCredits(oldPromoMaxCredits);
        setNewPromoMaxCreditAccount(oldPromoMaxCreditsAccount);
        oldPromoCreditEndDate ? setRadioSelected('date') : setRadioSelected('duration');
    }

    const checkEndDateChange = (oldEnd, newEnd) => {
        // If oldEnd was null and newEnd is a dayjs, OR if oldEnd was a dayjs and newEnd has changed, return true.
        return (!oldEnd && newEnd instanceof dayjs) || (oldEnd instanceof dayjs && !oldEnd.isSame(newEnd, 'day'));
    }

    const checkCreditDefaultEndDateChange = (oldEnd, newEnd) => {
        // If the old promo credit end date was null, but the new promo credit end date exists, return true.
        return ((!oldEnd && newEnd instanceof dayjs) ||
            // OR if the old promo credit end date did exist, but is different from the new one, return true.
            (oldEnd instanceof dayjs && newEnd instanceof dayjs && !oldEnd.isSame(newEnd, 'day')));
    }

    const handleFinish = async () => {
        let requestData = {};

        // name.
        if (oldPromoName !== newPromoName) {
            requestData['name'] = newPromoName;
        }
        // promo_code.
        if (oldPromoCode !== newPromoCode) {
            requestData['promo_code'] = newPromoCode.toUpperCase();
        }
        // started_at.
        if (!oldPromoStartedAt.isSame(newPromoStartedAt, 'day')) {
            requestData['started_at'] = newPromoStartedAt ? formatTimestampStartOfDay(newPromoStartedAt.tz()) : null;
        }
        // ended_at.
        if (checkEndDateChange(oldPromoEndedAt, newPromoEndedAt)) {
            requestData['ended_at'] = newPromoEndedAt ? formatTimestampEndOfDay(newPromoEndedAt.tz()) : null;
        }
        // default_amount.
        if (oldPromoDefaultAmount !== newPromoDefaultAmount) {
            requestData['default_amount'] = newPromoDefaultAmount;
        }
        // credits_default_ended_at & credits_default_duration.
        if (checkCreditDefaultEndDateChange(oldPromoCreditEndDate, newPromoCreditEndDate)) {
            // If the credit default end date changed to a new dayjs, assign and format new default end date, make duration null.
            requestData['credits_default_ended_at'] = formatTimestampEndOfDay(newPromoCreditEndDate.tz());
            requestData['credits_default_duration'] = null;
        } else if (oldPromoCreditDuration !== newPromoCreditDuration) {
            // If the new promo credit duration is different from the old one, assign the new one and the default end date null.
            requestData['credits_default_ended_at'] = null;
            requestData['credits_default_duration'] = newPromoCreditDuration;
        }
        // credits_max_num_total.
        if (oldPromoMaxCredits !== newPromoMaxCredits) {
            requestData['credits_max_num_total'] = newPromoMaxCredits;
        }
        // credits_max_num_account.
        if (oldPromoMaxCreditsAccount !== newPromoMaxCreditAccount) {
            requestData['credits_max_num_account'] = newPromoMaxCreditAccount;
        }
        // If no changes, nothing to update.
        if (Object.keys(requestData).length === 0) {
            showMessages(nothingToUpdate, messageType.error);
            return;
        }

        await processPromoUpdate(requestData);
    }

    const processPromoUpdate = async (values) => {
        setLoading(true);
        try {
            const abortSignal = abortSignalRef.current;
            const response = await updatePromotion(values, promoID, abortSignal);
            if (response) {
                onClose();
                setTableRefresh(true);
                showMessages('Promotion successfully updated.', messageType.success);
            }
            setLoading(false);
        } catch (e) {
            showMessages(e.message, messageType.error);
            setLoading(false);
        }
    }

    return (
        <>
            <Button
                className="promotions-table-add-btn"
                type="link"
                onClick={onOpen}
            >
                Edit
            </Button>
            <Drawer
                title="Edit Promotion"
                className="drawer-wrapper"
                open={open}
                onClose={onClose}
                width={mobileMode ? "100%" : "45%"}
            >
                <Form
                    form={form}
                    className="promotion-form-drawer"
                    layout="vertical"
                    name="add-promotion"
                    onFinish={handleFinish}
                >
                    <Row className="full-width-row">
                        <Form.Item
                            name="name"
                            label="Name: "
                            rules={[{
                                required: true,
                                message: 'Please enter a name.',
                            }]}
                        >
                            <Input
                                placeholder="Type here"
                                onChange={e => setNewPromoName(e.target.value)}
                            />
                        </Form.Item>
                    </Row>
                    <Row className="full-width-row">
                        <Form.Item
                            name="promo_code"
                            label="Promo Code: "
                            rules={[{required: false}]}
                        >
                            <Input
                                placeholder="Type here"
                                onChange={e => setNewPromoCode(e.target.value)}
                            />
                        </Form.Item>
                    </Row>
                    <Row className="full-width-row">
                        <Form.Item
                            name="started_at"
                            label="Start Date: "
                            rules={[{
                                required: true,
                                message: 'Please enter a start date.',
                            }]}
                        >
                            <DatePicker
                                format={dateFormat}
                                placeholder="DD-MM-YYYY"
                                className="inherit-parent-width"
                                onChange={date => setNewPromoStartedAt(date)}
                            />
                        </Form.Item>
                    </Row>
                    <Row className="full-width-row">
                        <div className="end-date-container">
                            <Form.Item
                                name="ended_at"
                                label="End Date: "
                                rules={[{
                                    required: radioSelected === 'date',
                                    message: 'Please enter an end date.',
                                }]}
                                className="inherit-parent-width"
                            >
                                <DatePicker
                                    format={dateFormat}
                                    placeholder="DD-MM-YYYY"
                                    className="inherit-parent-width"
                                    onChange={date => setNewPromoEndedAt(date)}
                                />
                            </Form.Item>
                            <Button
                                type="link"
                                className="end-date-clear-button"
                                onClick={() => {
                                    // Clears End Date field.
                                    setNewPromoEndedAt(null);
                                    form.resetFields(['ended_at']);
                                }}
                            >
                                Clear
                            </Button>
                        </div>
                    </Row>
                    <Row className="full-width-row">
                        <Form.Item
                            name="default_amount"
                            label="Value of Promotional Credit: "
                            rules={[{
                                required: true,
                                type: 'number',
                                min: 0.01,
                                message: greaterThanZero,
                            }]}
                        >
                            <InputMoney
                                placeholder="0.00"
                                onChange={value => setNewPromoDefaultAmount(value)}
                            />
                        </Form.Item>
                    </Row>
                    <Row className="full-width-row">
                        <Form.Item
                            name="promo_credit_expire"
                            label="Promotional Credit Expiration "
                            rules={[{
                                required: true,
                                message: 'Please select an expiry date or duration.',
                            }]}
                        >
                            <Radio.Group
                                className="radio-group"
                                onChange={e => {
                                    // Reset value on radio change.
                                    form.resetFields(['credits_default_ended_at', 'credits_default_duration']);
                                    setNewPromoCreditEndDate(null);
                                    setNewPromoCreditDuration(null);
                                    setRadioSelected(e.target.value);
                                }}
                            >
                                <div className="radio-container">
                                    <Radio
                                        name="exp-date-radio"
                                        className="radio-button"
                                        value="date"
                                    >
                                        Date:
                                    </Radio>
                                    <Form.Item
                                        name="credits_default_ended_at"
                                        className="expiry-form-item"
                                        rules={[{
                                            required: radioSelected === 'date',
                                            message: 'Please select a default expiry date.'
                                        }]}
                                    >
                                        <DatePicker
                                            name="exp-date"
                                            className="inherit-parent-width"
                                            format={dateFormat}
                                            placeholder="DD-MM-YYYY"
                                            disabled={radioSelected === 'duration'}
                                            onChange={date => setNewPromoCreditEndDate(date)}
                                        />
                                    </Form.Item>
                                </div>
                                <div className="radio-container">
                                    <Radio
                                        name="exp-duration-radio"
                                        value="duration"
                                        className="radio-button"
                                    >
                                        Duration:
                                    </Radio>
                                    <Form.Item
                                        name="credits_default_duration"
                                        className="expiry-form-item"
                                        rules={[{
                                            required: radioSelected === 'duration',
                                            message: 'Please enter a duration.',
                                        },
                                        {
                                            type: 'number',
                                            min: 1,
                                            message: greaterThanOrEqualOne,
                                        }]}
                                    >
                                        <InputNumber
                                            name="exp-duration"
                                            className="inherit-parent-width"
                                            placeholder="Type Here"
                                            disabled={radioSelected === 'date'}
                                            onChange={value => setNewPromoCreditDuration(value)}
                                            addonAfter="days"
                                        />
                                    </Form.Item>
                                </div>
                            </Radio.Group>
                        </Form.Item>
                    </Row>
                    <Row className="full-width-row">
                        <Form.Item
                            name="credits_max_num_total"
                            label="Maximum promotional credits: "
                            extra="Please leave it blank if there is no limit."
                            rules={[{
                                required: false,
                                type: 'number',
                                min: 1,
                                message: greaterThanOrEqualOne,
                            }]}
                        >
                            <InputNumber
                                placeholder="Type here"
                                className="inherit-parent-width"
                                onChange={value => setNewPromoMaxCredits(value)}
                            />
                        </Form.Item>
                    </Row>
                    <Row className="max-credits-padding">
                        <Form.Item
                            name="credits_max_num_account"
                            label="Maximum promotional credits per account: "
                            extra="Please leave it blank if there is no limit."
                            rules={[{
                                required: false,
                                type: 'number',
                                min: 1,
                                message: greaterThanOrEqualOne,
                            }]}
                        >
                            <InputNumber
                                placeholder="Type here"
                                className="inherit-parent-width"
                                onChange={value => setNewPromoMaxCreditAccount(value)}
                            />
                        </Form.Item>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Item>
                                <Button
                                    className="cancel-button"
                                    onClick={onClose}
                                >
                                    Cancel
                                </Button>
                                <Button loading={loading} type="primary" htmlType="submit">Edit Promotion</Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Drawer>
        </>
    )
}

export default PromotionEditDrawer;
